import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Cell, LabelList } from 'recharts';
import './dashboard.css';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../PropertyManagement/services/apiService';
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';

const Dashboard = () => {
  const [selectedFilter, setSelectedFilter] = useState('4');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  let navigate = useNavigate();
  const { currencyPrecision } = useCurrency();

  const ThirdFilter = [
    { value: '0', label: 'Day' },
    { value: '1', label: 'Week' },
    { value: '2', label: 'Month' },
    { value: '3', label: 'Year' },
    { value: '4', label: 'All Time' },
  ];

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const apiservice = new ApiService();
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiservice.get('GET_DASHBOARD_STATS');
      if (response.data.statusCode === 200) setDashboardData(response.data.result);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter.value);
  };

  // Icon components
  const BuildingIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M3 21h18"></path>
      <path d="M5 21V7l8-4v18"></path>
      <path d="M19 21V11l-6-4"></path>
      <rect x="9" y="9" width="4" height="4"></rect>
      <rect x="9" y="14" width="4" height="4"></rect>
      <rect x="14" y="14" width="4" height="4"></rect>
    </svg>
  );

  const HomeIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  );

  const KeyIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
    </svg>
  );

  const UnRenewLeaseIcon = ({ color }) => (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Key */}
      <circle cx="7" cy="15" r="3" />
      <path d="M10 15h6l2 2 2-2" />

      {/* Clock (Expired) */}
      <circle cx="17" cy="7" r="3" />
      <path d="M17 5v2l1 1" />
    </svg>
  );

  const RefreshIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <polyline points="23 4 23 10 17 10"></polyline>
      <polyline points="1 20 1 14 7 14"></polyline>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
  );

  const CheckIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
  );

  const ClockIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  );

  const CalendarIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  );

  const ReservedIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <rect x="3" y="7" width="18" height="14" rx="2"></rect>
      <path d="M3 7l9-4 9 4"></path>
      <polyline points="9 14 11 16 15 12"></polyline>
    </svg>
  );

  const ScaleIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M16 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z"></path>
      <path d="M2 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z"></path>
      <path d="M7 21h10"></path>
      <path d="M12 3v18"></path>
      <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"></path>
    </svg>
  );

  const TrendingUpIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
      <polyline points="17 6 23 6 23 12"></polyline>
    </svg>
  );

  const AEDIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill={color} stroke="none" fontSize="12" fontWeight="bold">
        {currencyPrecision?.code}
      </text>
    </svg>
  );

  const UsersIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  );

  const ChartIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10"></line>
      <line x1="12" y1="20" x2="12" y2="4"></line>
      <line x1="6" y1="20" x2="6" y2="14"></line>
    </svg>
  );

  const GavelIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0M3.124 7.5A8.969 8.969 0 015.292 3m13.416 0a8.969 8.969 0 012.168 4.5"></path>
    </svg>
  );

  // Parse agreement total value to handle comma-separated format
  const parseAgreementTotal = (total) => {
    if (typeof total === 'string') {
      return parseFloat(total.replace(/,/g, ''));
    }
    return parseFloat(total);
  };
  // Bounce cheque details - Creating property breakdown from total
  const bounceDetailsBreakdown = [
    { property: 'OUD METHA BUILDING (B)', count: 7, amount: 172000 },
    { property: 'AL BADAA BUILDING (C)', count: 6, amount: 85400 },
    { property: 'AL GARHOUD BUILDING', count: 1, amount: 53200 },
    { property: 'INTERNATIONAL CITY (CBD 4)', count: 5, amount: 223200 },
  ];
  // First Row Stats Cards - Using actual API fields
  const firstRowCards = dashboardData
    ? [
      {
        id: 1,
        value: dashboardData.leaseStatusCounts?.total || 0,
        label: 'Total Units',
        sublabel: 'All Properties',
        color: '#E0E7FF',
        iconColor: '#6366F1',
        icon: BuildingIcon,
      },
      {
        id: 14,
        value: dashboardData.leaseStatusCounts?.leaseTotal || 0,
        label: 'Lease Units',
        sublabel: 'From all Properties',
        color: '#FFEDD5',
        iconColor: '#FB923C',
        icon: KeyIcon,
      },
      {
        id: 15,
        value: dashboardData.leaseStatusCounts?.pmTotal || 0,
        label: 'PM Units',
        sublabel: 'From all Properties',
        color: '#a2fff6ff',
        iconColor: '#4ee6f0ff',
        icon: KeyIcon,
      },
      {
        id: 2,
        value: dashboardData.leaseCounts?.availableUnitsCount || 0,
        label: 'Available Units',
        sublabel: 'Ready to Lease',
        color: '#D1FAE5',
        iconColor: '#10B981',
        icon: HomeIcon,
      },
      {
        id: 7,
        value: dashboardData.leaseCounts?.reservedUnitsCount || 0,
        label: 'Reserved Units',
        sublabel: 'Currently Reserved',
        color: '#FEF3C7',
        iconColor: '#F59E0B',
        icon: ReservedIcon,
      },
      {
        id: 5,
        value: dashboardData.leaseStatusCounts?.active || 0,
        label: 'Currently Active',
        sublabel: 'Active Leases',
        color: '#FCE7F3',
        iconColor: '#EC4899',
        icon: CheckIcon,
      },
      {
        id: 4,
        value: dashboardData.leaseStatusCounts?.renewed || 0,
        label: 'Renewed Units',
        sublabel: 'This Month',
        color: '#E5FFE5',
        iconColor: '#4CAF50',
        icon: RefreshIcon,
      },
    ]
    : [];

  // Second Row Stats Cards - Using actual API fields
  const secondRowCards = dashboardData
    ? [
      {
        id: 17,
        value: dashboardData.leaseCounts?.unRenewedleasesCount || 0,
        label: 'UnRenewed Lease',
        sublabel: 'From all Properties',
        color: '#a2fff6ff',
        iconColor: '#4ee6f0ff',
        icon: UnRenewLeaseIcon,
      },
      {
        id: 8,
        value: dashboardData.leaseCounts?.leaseExpiryCount30Days || 0,
        label: '30 Days Expiry',
        sublabel: 'Expiring Soon',
        color: '#FFE5E5',
        iconColor: '#FF6B6B',
        icon: CalendarIcon,
      },
      {
        id: 9,
        value: dashboardData.leaseCounts?.leaseExpiryCount60Days || 0,
        label: '60 Days Expiry',
        sublabel: 'Need Attention',
        color: '#FFF4E5',
        iconColor: '#FFB366',
        icon: CalendarIcon,
      },
      {
        id: 10,
        value: dashboardData.leaseCounts?.leaseExpiryCount90Days || 0,
        label: '90 Days Expiry',
        sublabel: 'Plan Ahead',
        color: '#E5F3FF',
        iconColor: '#3B82F6',
        icon: CalendarIcon,
      },
      // {
      //   id: 11,
      //   value: dashboardData.bounceChequeDetails?.totalCount || 0,
      //   label: 'Bounced Cheques',
      //   sublabel: `${currencyPrecision?.code} ${(dashboardData.bounceChequeDetails?.totalAmount?.toFixed(currencyPrecision?.decimalNumbers) || 0).toLocaleString(undefined, {
      //     minimumFractionDigits: 2,
      //     maximumFractionDigits: 2,
      //   })}`,
      //   color: '#F0E5FF',
      //   iconColor: '#9C27B0',
      //   icon: ScaleIcon,
      // },
      {
        id: 12,
        value: dashboardData.leaseCounts?.pendingRenewalCount || 0,
        label: 'Pending Renewals',
        sublabel: 'Awaiting Renewal',
        color: '#E5FFE5',
        iconColor: '#4CAF50',
        icon: TrendingUpIcon,
      },
      {
        id: 6,
        value: dashboardData.leaseCounts?.leaseTerminatedThisMonth || 0,
        label: 'Terminated This Month',
        sublabel: 'Current Period',
        color: '#cbb0d9ff',
        iconColor: '#dc95ffff',
        icon: ClockIcon,
      },
      {
        id: 13,
        value: `${currencyPrecision?.code} ${(dashboardData.leaseCounts?.tenantDueAmount?.toFixed(currencyPrecision?.decimalNumbers) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        label: 'Tenant Dues',
        sublabel: `${dashboardData.leaseCounts?.tenantDueCount || 0} Pending Payments`,
        color: '#FFE5FF',
        iconColor: '#E91E63',
        icon: AEDIcon,
        isAmountString: true,
      },
    ]
    : [];

  // Transform monthlyInvoices data for chart
  const invoiceData =
    dashboardData && dashboardData.monthlyInvoices
      ? Object.entries(dashboardData.monthlyInvoices).map(([month, value]) => ({
        month: month.charAt(0).toUpperCase() + month.slice(1).substring(0, 3),
        value: Math.round(value / 1000000), // Convert to millions for display
        fullValue: value,
      }))
      : [];

  // Transform monthlyTenants data for area chart
  const tenantsData = dashboardData
    ? [
      { month: 'Month 1', lastMonth: 0, thisMonth: 0 },
      { month: 'Month 2', lastMonth: 0, thisMonth: 0 },
      { month: 'Month 3', lastMonth: 0, thisMonth: 0 },
      { month: 'Month 4', lastMonth: 0, thisMonth: 0 },
      { month: dashboardData.monthlyCustomers.previousMonthName, lastMonth: dashboardData.monthlyCustomers.previousMonthCount, thisMonth: 0 },
      {
        month: dashboardData.monthlyCustomers.currentMonthName,
        lastMonth: dashboardData.monthlyCustomers.previousMonthCount,
        thisMonth: dashboardData.monthlyCustomers.currentMonthCount,
      },
    ]
    : [];

  // Transform cheque deposits for monthly graph - Using actual API data
  const chequeMonthlyData =
    dashboardData && dashboardData.chequeDeposits
      ? [
        { month: 'Jan', amount: Math.round(dashboardData.chequeDeposits.janAmount / 1000000), count: dashboardData.chequeDeposits.janCount },
        { month: 'Feb', amount: Math.round(dashboardData.chequeDeposits.febAmount / 1000000), count: dashboardData.chequeDeposits.febCount },
        { month: 'Mar', amount: Math.round(dashboardData.chequeDeposits.marAmount / 1000000), count: dashboardData.chequeDeposits.marCount },
        { month: 'Apr', amount: Math.round(dashboardData.chequeDeposits.aprAmount / 1000000), count: dashboardData.chequeDeposits.aprCount },
        { month: 'May', amount: Math.round(dashboardData.chequeDeposits.mayAmount / 1000000), count: dashboardData.chequeDeposits.mayCount },
        { month: 'Jun', amount: Math.round(dashboardData.chequeDeposits.junAmount / 1000000), count: dashboardData.chequeDeposits.junCount },
        { month: 'Jul', amount: Math.round(dashboardData.chequeDeposits.julAmount / 1000000), count: dashboardData.chequeDeposits.julCount },
        { month: 'Aug', amount: Math.round(dashboardData.chequeDeposits.augAmount / 1000000), count: dashboardData.chequeDeposits.augCount },
        { month: 'Sep', amount: Math.round(dashboardData.chequeDeposits.sepAmount / 1000000), count: dashboardData.chequeDeposits.sepCount },
      ].filter((item) => item.count > 0) // Only show months with data
      : [];

  // Quarterly data for Lease Status Overview
  const getQuarterlyLeaseData = () => {
    if (!dashboardData) return [];

    // Using current data for Q4 and creating reasonable historical data
    const currentActive = dashboardData.leaseStatusCounts.active || 0;
    const currentRenewed = dashboardData.leaseStatusCounts.renewed || 0;
    const currentTerminated = dashboardData.leaseStatusCounts.terminated || 0;

    return [
      { quarter: 'Q1', active: Math.round(currentActive * 0.85), renewed: 155, terminated: 124 },
      { quarter: 'Q2', active: Math.round(currentActive * 0.9), renewed: 202, terminated: 83 },
      { quarter: 'Q3', active: Math.round(currentActive * 0.95), renewed: 180, terminated: 91 },
      { quarter: 'Q4', active: currentActive, renewed: currentRenewed, terminated: currentTerminated },
    ];
  };

  // Format date for expiring leases
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
  };

  // Navigate function
  const handleListing = (selectedRow) => {
    if (selectedRow?.id === 12 || selectedRow?.id === 13) {
      return
    } else {
      navigate(`/listing`, { state: selectedRow?.id });
    }
  };
  const handleExpListing = (selectedRow) => {
    
    navigate(`/listing`, { state: selectedRow });
  };
  // Calculate total legal cases
  const totalLegalCases = dashboardData?.legalcase ? dashboardData.legalcase.reduce((sum, item) => sum + item.legalcaseCount, 0) : 0;

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading dashboard data...</div>;
  }

  if (!dashboardData) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>No data available</div>;
  }

  return (
    <>
      <div className="dashboard-container">
        {/* Filter Pills */}
        <div className='d-flex  justify-content-between'>
          <div className='center'><h5>Dashboard</h5></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <nav
              style={{
                display: 'flex',
                gap: '4px',
                backgroundColor: 'white',
                padding: '4px',
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
              }}
            >
              {ThirdFilter?.map((filter) => (
                <button
                  key={filter?.value}
                  onClick={() => handleFilterChange(filter)}
                  style={{
                    padding: '4px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: selectedFilter === filter.value ? '#8B5CF6' : 'transparent',
                    color: selectedFilter === filter.value ? 'white' : '#6B7280',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                >
                  {filter?.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* First Row Stats Cards */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #E5E7EB',
          }}
        >
          <div className="stats-grid2">
            {firstRowCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.id} onClick={() => handleListing(card)} className="stat-card" style={{ backgroundColor: card.color }}>
                  <div className="stat-icon-wrapper">
                    <Icon color={card.iconColor} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{card.value.toLocaleString()}</div>
                    <div className="stat-label">{card.label}</div>
                    <div className="stat-sublabel">{card.sublabel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Second Row Stats Cards */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '20px',
            marginBottom: '32px',
            border: '1px solid #E5E7EB',
          }}
        >
          <div className="stats-grid2">
            {secondRowCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={`second-${card.id}`} onClick={() => handleListing(card)} className="stat-card" style={{ backgroundColor: card.color }}>
                  <div className="stat-icon-wrapper">
                    <Icon color={card.iconColor} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{card.isAmountString ? card.value : card.value.toLocaleString()}</div>
                    <div className="stat-label">{card.label}</div>
                    <div className="stat-sublabel">{card.sublabel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Three Charts Row */}
        <div className="widgets-grid-mix">
          {/* Invoice By Month with amounts on bars */}
          <div className="widget-card" style={{ height: '400px' }}>
            <div className="widget-title">
              Invoice By Month
              <div className="icon-badge" style={{ backgroundColor: '#FFEDD5' }}>
                <AEDIcon color="#FB923C" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={invoiceData} margin={{ top: 20, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  width={60}
                  label={{ value: 'Amount (Millions)', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#9CA3AF' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    fontSize: '12px',
                  }}
                  formatter={(value, name) => [`${currencyPrecision?.code} ${(value * 1000000).toLocaleString()}`, 'Invoice Amount']}
                />
                <Bar dataKey="value" fill="#6366F1" radius={[4, 4, 0, 0]} animationDuration={800}>
                  <LabelList dataKey="value" position="top" style={{ fontSize: '10px', fill: '#6B7280' }} formatter={(value) => `${value}M`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Expiring Leases */}
          <div className="widget-card">
            <div className="widget-title">
              Expiring Leases <span style={{ fontSize: '12px', color: '#9CA3AF', fontWeight: '400' }}>( Next 30 Days )</span>
            </div>
            <div className="expiring-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {dashboardData.expiringLeases && dashboardData.expiringLeases.length > 0 ? (
                dashboardData.expiringLeases.slice(0, 13).map((item, index) => (
                  <div key={index} className="expiring-item" style={{ justifyContent: 'space-between' }} >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: "pointer" }} onClick={() => handleExpListing(item)}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={item.expiringLeaseCount <= 3 ? '#10B981' : item.expiringLeaseCount <= 6 ? '#F59E0B' : '#EF4444'}
                        strokeWidth="2"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      <div style={{ flex: 1 }}>
                        <div className="item-name" style={{ fontSize: '10px', fontWeight: '500', marginBottom: '2px' }}>
                          {item.propertyName}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        backgroundColor: item.expiringLeaseCount <= 3 ? '#D1FAE5' : item.expiringLeaseCount <= 6 ? '#FFF4E5' : '#FEE2E2',
                        color: '#A855F7',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        minWidth: '35px',
                        textAlign: 'center',
                      }}
                    >
                      {item.expiringLeaseCount}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px' }}>No expiring leases</div>
              )}
            </div>
          </div>
        </div>

        {/* Three Charts Row - Monthly Tenants, Recent Agreements, Cheque Deposits */}
        <div className="widgets-grid-2">
          {/* Monthly Tenants */}
          <div className="widget-card">
            <div className="widget-title">
              Monthly Tenants
              <div className="icon-badge" style={{ backgroundColor: '#F3E8FF' }}>
                <UsersIcon color="#A855F7" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={tenantsData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorLast" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorThis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorThis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f6b53bff" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f6b53bff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="lastMonth" stroke="#10B981" fillOpacity={1} fill="url(#colorLast)" strokeWidth={2} />
                <Area type="monotone" dataKey="thisMonth" stroke="#3B82F6" fillOpacity={1} fill="url(#colorThis)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
            <div className="tenants-values">
              <div className="tenant-value-box">
                <div className="value-label">
                  <div className="legend-dot" style={{ backgroundColor: '#10B981' }}></div>
                  <span className="legend-text">{dashboardData.monthlyCustomers.previousMonthName}</span>
                </div>
                <div className="value-amount">{dashboardData.monthlyCustomers.previousMonthCount}</div>
              </div>
              <div className="tenant-value-box">
                <div className="value-label">
                  <div className="legend-dot" style={{ backgroundColor: '#3B82F6' }}></div>
                  <span className="legend-text">{dashboardData.monthlyCustomers.currentMonthName}</span>
                </div>
                <div className="value-amount">{dashboardData.monthlyCustomers.currentMonthCount}</div>
              </div>
            </div>
          </div>

          {/* Recent Agreements */}
          <div className="widget-card">
            <div className="widget-title">
              Recent Agreements <span style={{ fontSize: '12px', color: '#9CA3AF' }}>( Latest )</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {dashboardData.agreements && dashboardData.agreements.length > 0 ? (
                dashboardData.agreements.slice(0, 5).map((agreement, index) => (
                  <div
                    key={agreement.lease_No}
                    style={{
                      display: 'flex', cursor: "pointer",
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #F3F4F6',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '60%' }}>
                      <div
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: '#10B981',
                        }}
                      ></div>
                      <span style={{ fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {agreement.cust1_Name}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#6366F1',
                        backgroundColor: '#EEF2FF',
                        padding: '2px 10px',
                        borderRadius: '10px',
                      }}
                    >
                      {currencyPrecision?.code} {parseAgreementTotal(agreement.total)?.toFixed(currencyPrecision?.decimalNumbers).toLocaleString()}
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px' }}>No recent agreements</div>
              )}
            </div>
          </div>

          {/* Cheque Deposits Monthly Graph */}
          {/* <div className="widget-card">
            <div className="widget-title">
              Cheque Deposits Monthly
              <div className="icon-badge" style={{ backgroundColor: '#E0E7FF' }}>
                <AEDIcon color="#6366F1" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chequeMonthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9CA3AF' }}
                  label={{ value: 'Millions', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#9CA3AF' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                  }}
                  formatter={(value, name) => {
                    if (name === 'amount') return [`${currencyPrecision?.code} ${(value * 1000000)?.toFixed(currencyPrecision?.decimalNumbers).toLocaleString()}`, 'Amount'];
                    return [value, 'Count'];
                  }}
                />
                <Line type="monotone" dataKey="amount" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1', r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div> */}
        </div>

        {/* Bounce Cheque Details, Legal Cases & Lease Status Overview */}
        <div className="widgets-grid-2">
          {/* Bounce Cheque Details */}
          {/* <div className="widget-card">
            <div className="widget-title">
              Bounce Cheque Details
              <div className="icon-badge" style={{ backgroundColor: '#FEE2E2' }}>
                <AEDIcon color="#EF4444" />
              </div>
            </div>
            <table className="agreements-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th style={{ textAlign: 'center' }}>Count</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {bounceDetailsBreakdown.length > 0 ? (
                  bounceDetailsBreakdown.map((item, index) => (
                    <tr key={index}>
                      <td>{item.property}</td>
                      <td style={{ textAlign: 'center' }}>{item.count}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ color: '#EF4444', fontWeight: '600' }}>{currencyPrecision?.code} {item.amount?.toFixed(currencyPrecision?.decimalNumbers).toLocaleString()}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF' }}>
                      {dashboardData.bounceChequeDetails?.totalCount > 0 ? 'Property breakdown not available' : 'No bounced cheques'}
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #E5E7EB', fontWeight: '600' }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'center' }}>{dashboardData.bounceChequeDetails?.totalCount || 0}</td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ color: '#EF4444', fontSize: '14px' }}>
                      {currencyPrecision?.code}{' '}
                      {(dashboardData.bounceChequeDetails?.totalAmount?.toFixed(currencyPrecision?.decimalNumbers) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div> */}

          {/* Legal Cases */}
          <div className="widget-card">
            <div className="widget-title">
              Legal Cases
              <div className="icon-badge" style={{ backgroundColor: '#FEF3C7' }}>
                <GavelIcon color="#F59E0B" />
              </div>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table className="agreements-table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th style={{ textAlign: 'center' }}>Cases</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.legalcase && dashboardData.legalcase.length > 0 ? (
                    dashboardData.legalcase.slice(0, 6).map((item, index) => (
                      <tr key={index}>
                        <td style={{ fontSize: '12px' }}>{item.propertyName}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            style={{
                              backgroundColor: item.legalcaseCount >= 5 ? '#FEE2E2' : item.legalcaseCount >= 3 ? '#FFF4E5' : '#E5F3FF',
                              color: item.legalcaseCount >= 5 ? '#EF4444' : item.legalcaseCount >= 3 ? '#F59E0B' : '#3B82F6',
                              padding: '2px 10px',
                              borderRadius: '10px',
                              fontSize: '12px',
                              fontWeight: '600',
                            }}
                          >
                            {item.legalcaseCount}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF' }}>
                        No legal cases
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E5E7EB', fontWeight: '600' }}>
                    <td>Total Cases</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: '#F59E0B', fontSize: '14px' }}>{totalLegalCases}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Lease Status Overview - Quarterly */}
          <div className="widget-card">
            <div className="widget-title">
              Lease Status Overview (Quarterly)
              <div className="icon-badge" style={{ backgroundColor: '#F3E8FF' }}>
                <ChartIcon color="#A855F7" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={getQuarterlyLeaseData()} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="quarter" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} domain={[0, 'dataMax + 10']} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="active" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="renewed" fill="#6366F1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="terminated" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: '#10B981', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>Active</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: '#6366F1', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>Renewed</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: '#EF4444', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>Terminated</span>
              </div>
            </div>
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E5E7EB', textAlign: 'center' }}>
              <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '600' }}>Total Leases: </span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: '#8B5CF6' }}>{dashboardData.leaseStatusCounts.total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;